/**
 * The invite form asks "Who will be completing this form?" and the client's profile asks the same
 * on its step 1. Until 16 Oct 2026 the adviser's answer went only to the demo case: a client invited
 * as "a family member" opened step 1 to find "I am the person seeking advice" selected.
 */
export type InviteCompletingAs = 'self' | 'family_member' | 'epoa' | 'professional';
export type ProfileCompleter = 'client' | 'family' | 'eop' | 'professional';

export function completerFromInviteAnswer(answer: string | undefined | null): ProfileCompleter {
    switch (answer) {
        case 'family_member': return 'family';
        case 'epoa': return 'eop';
        case 'professional': return 'professional';
        default: return 'client';
    }
}

export interface InviteSeedInput {
    completingAs?: string | null;
    /** The person the invite is sent to: the client, or whoever is completing it for them. */
    firstName?: string;
    lastName?: string;
    /** The completer's relationship to the client ("Daughter", "Attorney under EPOA"). */
    relationship?: string;
}

export interface InviteFileSeed {
    completer: ProfileCompleter;
    client: { firstName: string; lastName: string };
    completerFirstName?: string;
    completerLastName?: string;
    completerRelationship?: string;
    /** What the file is called in the portal: the client where known, else the person invited. */
    displayName: string;
}

/**
 * The names on an invite, put where the profile keeps them. When the client completes it themselves,
 * the name on the invite is the client's. When a family member, attorney or professional does, the
 * name on the invite is THEIRS — it goes to step 1's "your details", with the relationship, and the
 * client is left for the profile to name (step 2 asks, and for a couple there are two). Owner, 16 Oct
 * 2026: a family member opened step 1 to blank "your details" fields, and found their own name on the
 * client.
 */
export function inviteFileSeed(input: InviteSeedInput): InviteFileSeed {
    const t = (v: string | undefined) => (v || '').trim();
    const completer = completerFromInviteAnswer(input.completingAs);
    const invitee = { firstName: t(input.firstName), lastName: t(input.lastName) };
    const join = (p: { firstName: string; lastName: string }) => [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
    if (completer === 'client') {
        return { completer, client: invitee, displayName: join(invitee) };
    }
    // The file is called after the person invited until the profile names the client.
    return {
        completer,
        client: { firstName: '', lastName: '' },
        completerFirstName: invitee.firstName || undefined,
        completerLastName: invitee.lastName || undefined,
        completerRelationship: t(input.relationship) || undefined,
        displayName: join(invitee),
    };
}
